import React, { useState } from 'react';
import { Alert, Props as AlertProps } from './Alert';
import { getAlertToaster, type ToastPosition } from './toaster';
import { type AlertToastMeta } from './AlertToaster';

type Props = {
  id: string | number;
  duration?: number | null;
} & Omit<AlertProps, 'variant'>;

export function useAlert(
  variant: AlertProps['variant'] = 'success',
  context: 'toast' | 'inline' = 'toast',
  position: ToastPosition = 'top-right',
  defaultDuration?: number | null
) {
  const toaster = getAlertToaster(position);

  const [isVisible, setIsVisible] = useState(true);

  return React.useCallback(
    ({ id, title, description, duration, isClosable = true, showIcon = true, actions, onClose = () => {} }: Props) => {
      const handleOnClose = (e?: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
        setIsVisible(false);
        onClose(e);
      };

      // context is inline
      if (context === 'inline') {
        if (isVisible) {
          return (
            <Alert
              variant={variant}
              title={title}
              description={description}
              isClosable={isClosable}
              showIcon={showIcon}
              actions={actions}
              onClose={handleOnClose}
            />
          );
        }

        return null;
      }

      // otherwise toast
      const stringId = String(id);
      if (toaster.isVisible(stringId)) {
        return null;
      }

      const resolvedDuration = duration !== undefined ? duration : defaultDuration;

      toaster.create({
        id: stringId,
        title,
        description,
        closable: isClosable,
        duration: resolvedDuration === null ? Infinity : (resolvedDuration ?? undefined),
        meta: { variant, showIcon, actions } satisfies AlertToastMeta,
        onStatusChange: details => {
          if (details.status === 'unmounted') {
            handleOnClose();
          }
        },
      });
      return null;
    },
    [variant, context, toaster, isVisible, defaultDuration]
  );
}
